/**
 * Copyright © IMPACT Commerce AB. All rights reserved.
 * See COPYING.txt for license details.
 */

(async () => {
  if (!customElements.get('ingrid-pdp-widget')) {
    class IngridPDPWidget extends HTMLElement {
      constructor() {
        super();

        this.authToken = this.getAttribute('data-token');
        this.product = JSON.parse(this.getAttribute('data-product'));
        if (!this.authToken) {
          return;
        }
      }

      connectedCallback() {
        const collapsibleGroup = this.closest('[data-collapsible-group]');

        if (collapsibleGroup) {
          if (collapsibleGroup.classList.contains('collapsible-is-open')) {
            this.init();
          }

          this._classObserver = new MutationObserver(() => {
            if (collapsibleGroup.classList.contains('collapsible-is-open')) {
              this.init();
              this._showIframe();
            } else {
              this._hideIframe();
            }
          });
          this._classObserver.observe(collapsibleGroup, { attributes: true, attributeFilter: ['class'] });
        } else {
          this.init();
        }
      }

      disconnectedCallback() {
        this._classObserver?.disconnect();
      }

      _hideIframe() {
        const iframe = this.querySelector('iframe');
        if (iframe) {
          iframe.style.pointerEvents = 'none';
          iframe.style.visibility = 'hidden';
        }
      }

      _showIframe() {
        const iframe = this.querySelector('iframe');
        if (iframe) {
          iframe.style.pointerEvents = '';
          iframe.style.visibility = '';
        }
      }

      async init() {
        if (this.rendered) return;
        this.rendered = true;
        this.isReady = await this.isIngridPDPWidgetApiReady();
        this.cart = await this.getCartId();
        if (!this.isReady || !this.cart) return;
        this.renderIngridPDPWidget();
      }

      /**
       * Renders the Ingrid PDP widget
       */
      renderIngridPDPWidget() {
        if (!window._ingridPDPWidgetApi || !this.isReady || !this.cart) {
          return;
        }

        const configObject = {
          country: this.getCountry(),
          locales: [this.getLocale()],
          currency: this.getCurrency(),
          auth_token: this.authToken,
          cart: {
            cart_id: this.cart.token,
            total_value: this.cart.total_price,
          },
          viewed_item: this.product,
        };

        window._ingridPDPWidgetApi.render(this.id, configObject);
      }

      /**
       * Checks if the Ingrid PDP widget API is ready
       * @returns {Promise<boolean>}
       */
      async isIngridPDPWidgetApiReady() {
        // Return directly if the Ingrid PDP widget API is already ready
        if (window._ingridPDPWidgetApi) {
          return true;
        }

        // Wait for the Ingrid PDP widget API to be ready with a maximum of 10 attempts
        return new Promise((resolve) => {
          let attempts = 0;
          const maxAttempts = 10;

          const intervalId = setInterval(() => {
            if (window._ingridPDPWidgetApi) {
              clearInterval(intervalId);
              resolve(true);
            } else {
              attempts++;
              if (attempts >= maxAttempts) {
                clearInterval(intervalId);
                resolve(false);
              }
            }
          }, 100);
        });
      }

      /**
       * Gets the cart id
       * @returns {Promise<string>}
       */
      async getCartId() {
        try {
          const response = await fetch(`${window.Shopify.routes.root}cart.js`, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
            },
          });
          return await response.json();
        } catch (error) {
          console.error('Ingrid Error fetching cart', error);
          return null;
        }
      }

      /**
       * Gets the locale
       * @returns {string}
       */
      getLocale() {
        return navigator.languages.filter((locale) => /[a-z]-[A-Z]/.test(locale))[0] || 'en-LV';
      }

      /**
       * Gets the currency
       * @returns {string}
       */
      getCurrency() {
        return Shopify?.currency?.active || 'EUR';
      }

      /**
       * Gets the country
       * @returns {string}
       */
      getCountry() {
        return Shopify?.country || 'EN';
      }
    }

    window.IngridPDPWidget = IngridPDPWidget;

    customElements.define('ingrid-pdp-widget', IngridPDPWidget);
  }
})();
